import { useQuery } from 'hooks/useCustomQuery';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/services/axios';
import { toast } from 'react-toastify';

export const useGetDialPlans = () => {
    return useQuery({
        queryKey: ['dialPlans'],
        queryFn: async () => {
            const response = await axios.get('/dialplans/');
            return response.data;
        },
    });
};

export const useGetDialPlan = (id) => {
    return useQuery({
        queryKey: ['dialPlan', id],
        queryFn: async () => {
            const response = await axios.get(`/dialplan/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useAddDialPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await axios.post('/dialplan/', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['dialPlans']);
            toast.success('Dial Plan added successfully');
        },
        onError: (error) => {
            toast.error(`Failed to add Dial Plan: ${error.message}`);
        },
    });
};

export const useUpdateDialPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await axios.put('/dialplan/', data);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['dialPlans']);
            queryClient.invalidateQueries(['dialPlan', data.id]);
            toast.success('Dial Plan updated successfully');
        },
        onError: (error) => {
            toast.error(`Failed to update Dial Plan: ${error.message}`);
        },
    });
};

export const useCopyDialPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, copyName }) => {
            // Follow the logic from TableAndModal
            const copyResponse = await axios.post(`/dialplan/${id}/copy`);
            const copiedPlan = copyResponse.data;

            // Then update the name
            const updateResponse = await axios.put('/dialplan/', {
                ...copiedPlan,
                name: copyName,
            });
            return updateResponse.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['dialPlans']);
            toast.success('Dial Plan copied successfully');
        },
        onError: (error) => {
            toast.error(`Failed to copy Dial Plan: ${error.message}`);
        },
    });
};

export const useGetRules = (dialPlanId) => {
    return useQuery({
        queryKey: ['rules', dialPlanId],
        queryFn: async () => {
            const response = await axios.get(`/rules/${dialPlanId}`);
            return response.data;
        },
        enabled: !!dialPlanId,
    });
};

export const useGetRule = (ruleId) => {
    return useQuery({
        queryKey: ['rule', ruleId],
        queryFn: async () => {
            const response = await axios.get(`/rule/${ruleId}`);
            return response.data;
        },
        enabled: !!ruleId,
    });
};

export const useAddRule = (dialPlanId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await axios.post('/rule/', {
                ...data,
                dialPlanID: dialPlanId,
                isActive: true,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['rules', dialPlanId]);
            toast.success('Rule added successfully');
        },
        onError: (error) => {
            toast.error(`Failed to add rule: ${error.message}`);
        },
    });
};

export const useUpdateRule = (dialPlanId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const response = await axios.put('/rule/', {
                ...data,
                dialPlanID: dialPlanId,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['rules', dialPlanId]);
            toast.success('Rule updated successfully');
        },
        onError: (error) => {
            toast.error(`Failed to update rule: ${error.message}`);
        },
    });
};
